import { api } from './core/api';
import type { DispatchRow } from '../components/dispatcher/dispatchTypes';
import type { DispatchSosAlert } from '../components/dispatcher/sosTypes';

export interface DispatchDashboardStats {
  activeRides: number;
  activeFood?: number;
  activeGrocery?: number;
  activePorter?: number;
  activeOnDemand?: number;
  activeDeliveries: number;
  activeServiceRequests: number;
  activeJobs: number;
  pendingRides?: number;
  pendingFood?: number;
  pendingGrocery?: number;
  pendingPorter?: number;
  pendingOnDemand?: number;
  pendingAssignments: number;
  scheduledBookings: number;
  onlineDrivers: number;
  offlineDrivers: number;
  busyDrivers: number;
  availableDeliveryPartners: number;
  availableServiceProviders: number;
  activeSos: number;
  cancellationsToday: number;
  revenueToday: number;
  earningsToday: number;
  driverUtilization: number;
  heatMapPoints: { lat: number; lng: number; weight: number }[];
}

export type DispatchHeatMapRange = 'live' | '1h' | '6h' | '24h' | 'today';
export type DispatchHeatMapService = 'all' | 'transportation' | 'food' | 'grocery';

export interface DispatchHeatMapData {
  range: DispatchHeatMapRange;
  service: DispatchHeatMapService;
  totalDemand: number;
  points: { lat: number; lng: number; weight: number }[];
}

export interface FleetMarker {
  id: string;
  type: 'driver' | 'delivery_partner' | 'service_provider';
  name: string;
  phone?: string;
  lat: number;
  lng: number;
  isOnline: boolean;
  isAvailable: boolean;
  status: string;
  vehicleNumber?: string;
  vehicleType?: string;
  rating?: number;
  speed?: number;
  batteryLevel?: number;
}

export async function getDispatchDashboard() {
  const res = await api.get('/dispatcher/dashboard');
  return res.data.data as DispatchDashboardStats;
}

export async function getDispatchHeatMap(params?: {
  range?: DispatchHeatMapRange;
  service?: DispatchHeatMapService;
}) {
  const res = await api.get('/dispatcher/heatmap', { params });
  return res.data.data as DispatchHeatMapData;
}

export async function getDispatchQueue(params?: { service?: string; status?: string; limit?: number }) {
  const res = await api.get('/dispatcher/queue', { params });
  return res.data.data as DispatchRow[];
}

export async function getDispatchFleet() {
  const res = await api.get('/dispatcher/fleet');
  return res.data.data as {
    drivers: FleetMarker[];
    partners: FleetMarker[];
    summary: Record<string, number>;
  };
}

export async function getDriverMonitoring(userId: string) {
  const res = await api.get(`/dispatcher/drivers/${userId}/monitoring`);
  return res.data.data as {
    userId: string;
    name: string;
    phone?: string;
    vehicleNumber?: string;
    vehicleType?: string;
    rating?: number;
    isOnline: boolean;
    isAvailable: boolean;
    status: string;
    location?: { coordinates?: [number, number] };
    speed?: number;
    batteryLevel?: number;
  } | null;
}

export async function autoAssignJob(payload: {
  serviceType: string;
  entityType: string;
  entityId: string;
  coordinates?: [number, number];
  isVip?: boolean;
}) {
  const res = await api.post('/dispatcher/auto-assign', payload);
  return res.data.data;
}

export async function manualAssignJob(payload: {
  serviceType: string;
  entityType: string;
  entityId: string;
  partnerId: string;
  mode?: 'manual' | 'reassign';
}) {
  const res = await api.post('/dispatcher/assign', payload);
  return res.data.data;
}

export async function searchDispatchPartners(params: {
  type: 'driver' | 'delivery_partner' | 'service_provider';
  q?: string;
  lat?: number;
  lng?: number;
}) {
  const res = await api.get('/dispatcher/partners/search', { params });
  return res.data.data;
}

export async function getDispatchLogs(params?: { entityId?: string; page?: number; limit?: number }) {
  const res = await api.get('/dispatcher/logs', { params });
  return res.data;
}

export async function getScheduledDispatches(status = 'pending') {
  const res = await api.get('/dispatcher/scheduled', { params: { status } });
  return res.data.data;
}

export async function listDispatchSos(params?: { scope?: 'active' | 'resolved' | 'all'; severity?: string }) {
  const res = await api.get('/dispatcher/sos', { params });
  return res.data.data as DispatchSosAlert[];
}

export async function acknowledgeSos(id: string) {
  const res = await api.put(`/dispatcher/sos/${id}/acknowledge`);
  return res.data.data;
}

export async function resolveSos(id: string, payload?: { incidentReport?: string; notes?: string }) {
  const res = await api.put(`/dispatcher/sos/${id}/resolve`, payload);
  return res.data.data;
}

export async function escalateSos(id: string) {
  const res = await api.put(`/dispatcher/sos/${id}/escalate`);
  return res.data.data;
}

export const ENTITY_TYPE_MAP: Record<string, string> = {
  transportation: 'transport',
  food: 'food_order',
  grocery: 'grocery_order',
  porter: 'porter_order',
  on_demand: 'on_demand_booking',
};
